/**
 * Integrytas Group — Email Distribution System (EDS)
 * Service: EdsSendService
 *
 * Orchestrates the campaign send pipeline:
 *
 *   1. enqueueJob(campaignId, actorId)
 *      → Creates an eds_send_job row (status = QUEUED)
 *      → Snapshots eligible recipients (excluding opted-out) into eds_send_job_recipient
 *
 *   2. processJob(jobId)
 *      → Iterates recipients in the job snapshot
 *      → For each: signs unsubscribe URL, substitutes merge tags, builds final HTML
 *      → Writes eds_email_log row (status = SENT or FAILED)
 *      → Updates job progress counters
 *
 *   3. aggregateStats(campaignId)
 *      → Returns rolled-up delivery stats for the Queue & Log dashboard
 *
 * ─── Actual email transport ──────────────────────────────────────────────────
 *
 *   This service does NOT call an SMTP server directly. The `sendEmail` helper
 *   at the bottom of this file is the integration point — swap it for your
 *   preferred provider (Resend, SendGrid, AWS SES, Supabase Edge Function, etc.)
 *
 *   The default stub logs to console and marks the send DELIVERED so the rest
 *   of the pipeline (logging, stats) works end-to-end in development.
 *
 * ─── Recipient filtering ─────────────────────────────────────────────────────
 *
 *   Excluded from every send:
 *     • eds_recipient.is_globally_opted_out = 'Y'
 *     • Matching GLOBAL row in eds_opt_out.is_active = 'Y'
 *     • Matching LIST-scoped row in eds_opt_out.is_active = 'Y' for this list
 *     • eds_recipient.is_active ≠ 'Y'
 *     • eds_list_recipient.is_active ≠ 'Y'
 */

import { apiDb } from '../../lib/api_client';
import { routedUpdateAs } from '../../lib/routed_writes';
import {
  substituteMergeTags,
  buildUnsubscribeFooter,
  hasUnsubscribeTag,
  type MergeContext,
} from './EdsCommonRoutine';
import { buildUnsubscribeUrl } from './EdsTokenService';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/** Base URL used when constructing unsubscribe links. Falls back to window.location.origin. */
function getAppBaseUrl(): string {
  if (typeof window !== 'undefined') return window.location.origin;
  return process.env.VITE_APP_URL ?? 'https://app.integrytas.com';
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CampaignStats {
  campaign_id:      string;
  campaign_name:    string;
  send_mode:        string;
  status:           string;
  total_recipients: number;
  sent_count:       number;
  delivered_count:  number;
  bounced_count:    number;
  opened_count:     number;
  clicked_count:    number;
  opted_out_count:  number;
  failed_count:     number;
  delivered_pct:    number;
  bounce_pct:       number;
  open_rate_pct:    number;
}

export interface EnqueueResult {
  ok: boolean;
  jobId?: string;
  recipientCount?: number;
  error?: string;
}

export interface ProcessResult {
  ok: boolean;
  sent?: number;
  failed?: number;
  error?: string;
}

// ---------------------------------------------------------------------------
// 1. Enqueue a job
// ---------------------------------------------------------------------------

/**
 * Snapshot eligible recipients and create a send job for the campaign.
 * Returns immediately — actual sending is done by processJob().
 */
export async function enqueueJob(
  campaignId: string,
  actorId: number | null,
): Promise<EnqueueResult> {
  const now = new Date().toISOString();

  // Load campaign + template
  const { data: campaign, error: campaignErr } = await apiDb
    .from('eds_campaign')
    .select(`
      campaign_id, campaign_name, recipient_list_id, template_id,
      subject_override, throttle_per_hour, send_mode,
      eds_email_template (
        template_id, subject_line, body_html, body_text,
        from_name, from_email, reply_to_email
      )
    `)
    .eq('campaign_id', campaignId)
    .single();

  if (campaignErr || !campaign) {
    return { ok: false, error: campaignErr?.message ?? 'Campaign not found.' };
  }

  // Collect eligible recipients
  const eligibleRecipients = await fetchEligibleRecipients(
    campaign.recipient_list_id as string,
    campaignId,
  );

  if (eligibleRecipients.length === 0) {
    return { ok: false, error: 'No eligible recipients found (all opted out or list is empty).' };
  }

  // Create the send job
  const { data: job, error: jobErr } = await apiDb
    .from('eds_send_job')
    .insert({
      campaign_id:        campaignId,
      status:             'QUEUED',
      total_recipients:   eligibleRecipients.length,
      sent_count:         0,
      failed_count:       0,
      created_at:         now,
      updated_at:         now,
      modified_by:        actorId,
    })
    .select('job_id')
    .single();

  if (jobErr || !job) {
    return { ok: false, error: jobErr?.message ?? 'Failed to create send job.' };
  }

  // Snapshot recipients into eds_send_job_recipient
  const snapshotRows = eligibleRecipients.map(r => ({
    job_id:       job.job_id,
    recipient_id: r.recipient_id,
    status:       'PENDING',
    created_at:   now,
    updated_at:   now,
    modified_by:  actorId,
  }));

  const { error: snapshotErr } = await apiDb
    .from('eds_send_job_recipient')
    .insert(snapshotRows);

  if (snapshotErr) {
    // Roll back the job
    await apiDb.from('eds_send_job').delete().eq('job_id', job.job_id);
    return { ok: false, error: snapshotErr.message };
  }

  // Update campaign status (§2; routine stamps modified_by + updated_at).
  await routedUpdateAs(actorId, 'eds_campaign', 'campaign_id', campaignId, { status: 'QUEUED' });

  return { ok: true, jobId: job.job_id, recipientCount: eligibleRecipients.length };
}

// ---------------------------------------------------------------------------
// 2. Process a job  (batch send)
// ---------------------------------------------------------------------------

/**
 * Process a send job.  Intended to be called from a Supabase Edge Function
 * or a server-side cron — but also works client-side for small lists.
 *
 * For large lists (>1000 recipients), call this from an Edge Function
 * that the scheduler triggers.
 */
export async function processJob(
  jobId: string,
  actorId: number | null,
): Promise<ProcessResult> {
  const now = new Date().toISOString();

  // Mark job in-progress
  await apiDb
    .from('eds_send_job')
    .update({ status: 'SENDING', started_at: now, updated_at: now, modified_by: actorId })
    .eq('job_id', jobId);

  // Load job + campaign + template
  const { data: job, error: jobErr } = await apiDb
    .from('eds_send_job')
    .select(`
      job_id, campaign_id, total_recipients,
      eds_campaign (
        campaign_id, campaign_name, subject_override,
        eds_email_template (
          subject_line, body_html, body_text,
          from_name, from_email, reply_to_email
        )
      )
    `)
    .eq('job_id', jobId)
    .single();

  if (jobErr || !job) {
    return { ok: false, error: jobErr?.message ?? 'Send job not found.' };
  }

  type JobTemplate = {
    subject_line: string | null;
    body_html: string | null;
    body_text: string | null;
    from_name: string;
    from_email: string;
    reply_to_email: string | null;
  };
  type JobCampaign = {
    campaign_id: string;
    campaign_name: string;
    subject_override: string | null;
    eds_email_template: JobTemplate | null;
  };
  const campaign = job.eds_campaign as JobCampaign;
  const template = campaign?.eds_email_template as JobTemplate;

  // Load pending recipients for this job
  const { data: pendingRows } = await apiDb
    .from('eds_send_job_recipient')
    .select(`
      job_recipient_id, recipient_id,
      eds_recipient ( recipient_id, email_address, first_name, last_name, company_name, merge_data )
    `)
    .eq('job_id', jobId)
    .eq('status', 'PENDING');

  type JobRecipient = {
    email_address: string;
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
    merge_data: Record<string, string> | null;
    recipient_id: string;
  };
  type PendingRow = {
    job_recipient_id: string;
    recipient_id: string;
    eds_recipient: JobRecipient;
  };
  const recipients = (pendingRows ?? []) as PendingRow[];
  let sent = 0;
  let failed = 0;
  const baseUrl = getAppBaseUrl();

  for (const row of recipients) {
    const r = row.eds_recipient as JobRecipient;
    const rowNow = new Date().toISOString();

    // Build unsubscribe URL
    const unsubUrl = await buildUnsubscribeUrl(baseUrl, r.recipient_id, campaign.campaign_id);

    // Build merge context
    const ctx: MergeContext = {
      first_name:      r.first_name ?? '',
      last_name:       r.last_name ?? '',
      email:           r.email_address,
      company_name:    r.company_name ?? '',
      campaign_name:   campaign.campaign_name,
      unsubscribe_url: unsubUrl ?? '#',
      ...(r.merge_data as Record<string, string> ?? {}),
    };

    // Merge tags into body
    let finalHtml = substituteMergeTags(template.body_html ?? '', ctx);

    // Auto-inject unsubscribe footer if tag was not in template
    if (!hasUnsubscribeTag(template.body_html ?? '')) {
      finalHtml += '\n' + buildUnsubscribeFooter(unsubUrl ?? '#');
    }

    const subjectLine: string = campaign.subject_override
      ? substituteMergeTags(campaign.subject_override, ctx)
      : substituteMergeTags(template.subject_line ?? '', ctx);

    // Attempt send
    let deliveryStatus: string;
    let sendError: string | null = null;

    try {
      await sendEmail({
        to:          r.email_address,
        fromName:    template.from_name,
        fromEmail:   template.from_email,
        replyTo:     template.reply_to_email ?? undefined,
        subject:     subjectLine,
        html:        finalHtml,
        text:        template.body_text
          ? substituteMergeTags(template.body_text, ctx)
          : undefined,
      });
      deliveryStatus = 'SENT';
      sent++;
    } catch (err: unknown) {
      deliveryStatus = 'FAILED';
      sendError = err instanceof Error ? err.message : String(err);
      failed++;
    }

    // Write email log
    await apiDb
      .from('eds_email_log')
      .insert({
        job_id:           jobId,
        campaign_id:      campaign.campaign_id,
        recipient_id:     r.recipient_id,
        to_email:         r.email_address,
        from_email:       template.from_email,
        subject_line:     subjectLine,
        delivery_status:  deliveryStatus,
        error_message:    sendError,
        sent_at:          deliveryStatus === 'SENT' ? rowNow : null,
        created_at:       rowNow,
        updated_at:       rowNow,
        modified_by:      actorId,
      });

    // Update snapshot row
    await apiDb
      .from('eds_send_job_recipient')
      .update({ status: deliveryStatus, processed_at: rowNow, updated_at: rowNow })
      .eq('job_recipient_id', row.job_recipient_id);
  }

  // Finalize job
  const finalStatus = failed === 0 ? 'COMPLETED' : sent === 0 ? 'FAILED' : 'COMPLETED_WITH_ERRORS';
  const completedNow = new Date().toISOString();

  await apiDb
    .from('eds_send_job')
    .update({
      status:         finalStatus,
      sent_count:     sent,
      failed_count:   failed,
      completed_at:   completedNow,
      updated_at:     completedNow,
      modified_by:    actorId,
    })
    .eq('job_id', jobId);

  // Update campaign status (§2; routine stamps modified_by + updated_at).
  await routedUpdateAs(actorId, 'eds_campaign', 'campaign_id', campaign.campaign_id, {
    status: 'SENT', sent_at: completedNow,
  });

  return { ok: true, sent, failed };
}

// ---------------------------------------------------------------------------
// 3. Stats aggregation
// ---------------------------------------------------------------------------

/**
 * Return rolled-up delivery statistics for a single campaign.
 * Reads from eds_email_log and eds_email_event.
 */
export async function aggregateCampaignStats(campaignId: string): Promise<CampaignStats | null> {
  const { data: campaign } = await apiDb
    .from('eds_campaign')
    .select('campaign_id, campaign_name, send_mode, status')
    .eq('campaign_id', campaignId)
    .single();

  if (!campaign) return null;

  // Log-level counts
  const { data: logs } = await apiDb
    .from('eds_email_log')
    .select('delivery_status')
    .eq('campaign_id', campaignId);

  const logRows = (logs ?? []) as { delivery_status: string }[];
  const total     = logRows.length;
  const sent      = logRows.filter(r => r.delivery_status !== 'PENDING').length;
  const delivered = logRows.filter(r => r.delivery_status === 'DELIVERED').length;
  const bounced   = logRows.filter(r => ['BOUNCED', 'HARD_BOUNCE', 'SOFT_BOUNCE'].includes(r.delivery_status)).length;
  const failed    = logRows.filter(r => r.delivery_status === 'FAILED').length;

  // Event-level counts (opens, clicks, unsubscribes)
  const { data: events } = await apiDb
    .from('eds_email_event')
    .select('event_type, log_id')
    .in('log_id', logRows.length > 0
      ? await fetchLogIds(campaignId)
      : []);

  const evRows = (events ?? []) as { event_type: string; log_id: string }[];
  const openLogIds  = new Set(evRows.filter(e => e.event_type === 'OPENED').map(e => e.log_id));
  const clickLogIds = new Set(evRows.filter(e => e.event_type === 'CLICKED').map(e => e.log_id));
  const optOuts   = evRows.filter(e => e.event_type === 'UNSUBSCRIBED').length;

  return {
    campaign_id:      campaign.campaign_id,
    campaign_name:    campaign.campaign_name,
    send_mode:        campaign.send_mode,
    status:           campaign.status,
    total_recipients: total,
    sent_count:       sent,
    delivered_count:  delivered,
    bounced_count:    bounced,
    opened_count:     openLogIds.size,
    clicked_count:    clickLogIds.size,
    opted_out_count:  optOuts,
    failed_count:     failed,
    delivered_pct:    sent > 0 ? Math.round((delivered / sent) * 100) : 0,
    bounce_pct:       sent > 0 ? Math.round((bounced  / sent) * 100) : 0,
    open_rate_pct:    delivered > 0 ? Math.round((openLogIds.size / delivered) * 100) : 0,
  };
}

/** Aggregate stats for ALL campaigns (for the dashboard overview). */
export async function listCampaignStats(): Promise<CampaignStats[]> {
  const { data: campaigns } = await apiDb
    .from('eds_campaign')
    .select('campaign_id')
    .order('created_at', { ascending: false });

  if (!campaigns) return [];

  const results = await Promise.all(
    (campaigns as { campaign_id: string }[]).map(c => aggregateCampaignStats(c.campaign_id)),
  );

  return results.filter((r): r is CampaignStats => r !== null);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface EligibleRecipient {
  recipient_id:    string;
  email_address:   string;
  first_name:      string | null;
  last_name:       string | null;
  company_name:    string | null;
  merge_data:      Record<string, unknown> | null;
}

/**
 * Return all active, non-opted-out recipients in a list.
 * Exclusions are applied in the database query for efficiency.
 */
async function fetchEligibleRecipients(
  listId: string,
  campaignId: string,
): Promise<EligibleRecipient[]> {
  // Get all opted-out recipient IDs for this list + globally
  const { data: optOuts } = await apiDb
    .from('eds_opt_out')
    .select('recipient_id')
    .eq('is_active', 'Y')
    .or(`opt_out_scope.eq.GLOBAL,and(opt_out_scope.eq.LIST,opt_out_scope_ref.eq.${listId})`);

  const excludedIds = new Set((optOuts ?? []).map((o: { recipient_id: string }) => o.recipient_id));

  // Also exclude anyone already in a sent log for this campaign (idempotency)
  const { data: alreadySent } = await apiDb
    .from('eds_email_log')
    .select('recipient_id')
    .eq('campaign_id', campaignId);

  (alreadySent ?? []).forEach((r: { recipient_id: string }) => excludedIds.add(r.recipient_id));

  // Fetch active list members
  const { data: members } = await apiDb
    .from('eds_list_recipient')
    .select(`
      recipient_id,
      eds_recipient (
        recipient_id, email_address, first_name, last_name,
        company_name, merge_data, is_globally_opted_out, is_active
      )
    `)
    .eq('list_id', listId)
    .eq('is_active', 'Y');

  type MemberRecipient = EligibleRecipient & {
    is_globally_opted_out: string;
    is_active: string;
  };
  type MemberRow = { eds_recipient: MemberRecipient | null };
  return (members as MemberRow[] ?? [])
    .map(m => m.eds_recipient)
    .filter(
      (r): r is MemberRecipient => !!r &&
           r.is_active === 'Y' &&
           r.is_globally_opted_out !== 'Y' &&
           !excludedIds.has(r.recipient_id),
    );
}

async function fetchLogIds(campaignId: string): Promise<string[]> {
  const { data } = await apiDb
    .from('eds_email_log')
    .select('log_id')
    .eq('campaign_id', campaignId);
  return (data ?? []).map((r: { log_id: string }) => r.log_id);
}

// ---------------------------------------------------------------------------
// Email transport stub — replace with your provider
// ---------------------------------------------------------------------------

interface EmailPayload {
  to:         string;
  fromName:   string;
  fromEmail:  string;
  replyTo?:   string;
  subject:    string;
  html:       string;
  text?:      string;
}

/**
 * Send a single email.
 *
 * STUB implementation — logs to console and resolves immediately.
 * Replace the body of this function with your email provider's SDK call:
 *
 *   Resend:   await resend.emails.send({ from, to, subject, html })
 *   SendGrid: await sgMail.send({ to, from, subject, html })
 *   SES:      await sesClient.send(new SendEmailCommand({ ... }))
 *   Supabase Edge Function:
 *     await fetch('https://<project>.functions.apiDb.co/send-email', { ... })
 */
async function sendEmail(payload: EmailPayload): Promise<void> {
  // ── REPLACE THIS STUB ────────────────────────────────────────────────────
  console.warn(
    `[EdsSendService] sendEmail → to=${payload.to}  subject="${payload.subject}"  from="${payload.fromName} <${payload.fromEmail}>"`,
  );
  // Simulate a small network delay in development
  await new Promise(resolve => setTimeout(resolve, 10));
  // ── END STUB ─────────────────────────────────────────────────────────────
}
